// API para persistir los chats del Asistente V2 en la tabla `agent_chats`.
//
// Columnas: id, user_id, id_company, title, messages (jsonb), created_at,
// updated_at. RLS por empresa (el front escribe con el token del usuario, así
// que cada uno solo toca los chats de su company); `updated_at` lo bumpea un
// trigger BEFORE UPDATE.
//
// IMPORTANTE: en `messages` se guardan los V2ChatMessage tal cual — SOLO con
// `image_ids` en las cards de resultado (NUNCA URLs firmadas, que expiran).
// Las URLs se vuelven a firmar al renderizar.

import { supabase } from '../../lib/supabase';
import type { V2ChatMessage } from './useV2Chat';

const TITLE_MAX = 80;

export interface AgentChatSummary {
  id: string;
  title: string | null;
  updated_at: string;
}

/** Crea un chat nuevo (primer mensaje) y devuelve su id. */
export async function createChat(args: {
  userId: string;
  companyId: string;
  title: string;
  messages: V2ChatMessage[];
}): Promise<string | null> {
  const title = args.title.trim().slice(0, TITLE_MAX);
  const { data, error } = await supabase
    .from('agent_chats')
    .insert({
      user_id: args.userId,
      id_company: args.companyId,
      title: title || null,
      messages: args.messages,
    })
    .select('id')
    .single();
  if (error) {
    console.error('[agentChatApi] createChat', error);
    return null;
  }
  return ((data as any)?.id as string) ?? null;
}

/** Actualiza los mensajes de un chat existente (updated_at lo bumpea el trigger). */
export async function updateChatMessages(
  chatId: string,
  messages: V2ChatMessage[],
): Promise<void> {
  const { error } = await supabase
    .from('agent_chats')
    .update({ messages })
    .eq('id', chatId);
  if (error) console.error('[agentChatApi] updateChatMessages', error);
}

/** Carga los mensajes de un chat por id (para abrir uno del historial). */
export async function loadChat(
  chatId: string,
): Promise<V2ChatMessage[] | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('messages')
    .eq('id', chatId)
    .single();
  if (error) {
    console.error('[agentChatApi] loadChat', error);
    return null;
  }
  const msgs = (data as any)?.messages;
  return Array.isArray(msgs) ? (msgs as V2ChatMessage[]) : [];
}

/** Suma créditos al tally del chat (costo de imágenes generadas). El costo
 *  del agente (LLM) lo suma el edge function `chat-agent`. */
export async function addChatCredits(
  chatId: string,
  credits: number,
): Promise<void> {
  if (credits <= 0) return;
  const { error } = await supabase.rpc('increment_agent_chat_credits', {
    p_chat_id: chatId,
    p_credits: credits,
  });
  if (error) console.error('[agentChatApi] addChatCredits', error);
}

/** Lee los créditos totales consumidos en un chat (agente + imágenes). */
export async function fetchChatCredits(chatId: string): Promise<number> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('total_credits_used')
    .eq('id', chatId)
    .single();
  if (error) {
    console.error('[agentChatApi] fetchChatCredits', error);
    return 0;
  }
  return Number((data as any)?.total_credits_used ?? 0);
}

/** Lista los chats de la empresa del usuario, recientes primero (historial). */
export async function listChats(): Promise<AgentChatSummary[]> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('id, title, updated_at')
    .order('updated_at', { ascending: false });
  if (error) {
    console.error('[agentChatApi] listChats', error);
    return [];
  }
  return (data as AgentChatSummary[]) ?? [];
}
